<script setup lang="ts">
import { computed } from 'vue';
import { Line } from 'vue-chartjs';
import { useMobile } from '@/composables/useMobile';
import type { MonthlyFinanceStats } from '@/api/financial-summaries';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  SubTitle,
  Tooltip,
  Legend,
  type TooltipItem,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, SubTitle, Tooltip, Legend);

interface Props {
  monthlyStats: MonthlyFinanceStats[];
}

const props = defineProps<Props>();

const { isPhone } = useMobile();

const DESKTOP_TITLE_SIZE = 20;
const PHONE_TITLE_SIZE = 16;
const DESKTOP_FONT_SIZE = 14;
const PHONE_FONT_SIZE = 12;
const DESKTOP_LEGEND_PADDING = 20;
const PHONE_LEGEND_PADDING = 8;

const chartData = computed(() => {
  const labels = props.monthlyStats.map(data => data.monthName);
  const incomeData = props.monthlyStats.map(data => data.expectedIncome);
  const costData = props.monthlyStats.map(data => data.expectedCost);

  return {
    labels,
    datasets: [
      {
        label: 'Cobros',
        data: incomeData,
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#4CAF50',
        pointBorderColor: '#4CAF50',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
      {
        label: 'Pagos',
        data: costData,
        borderColor: '#F44336',
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#F44336',
        pointBorderColor: '#F44336',
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };
});

// On phones: shrink the title/labels, tighten the legend, and drop the axis
// titles so the chart fits a short container. Desktop options are unchanged.
const chartOptions = computed(() => {
  const titleSize = isPhone.value ? PHONE_TITLE_SIZE : DESKTOP_TITLE_SIZE;
  const fontSize = isPhone.value ? PHONE_FONT_SIZE : DESKTOP_FONT_SIZE;
  const legendPadding = isPhone.value ? PHONE_LEGEND_PADDING : DESKTOP_LEGEND_PADDING;

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: legendPadding,
          font: {
            size: fontSize,
          },
        },
      },
      title: {
        display: true,
        text: 'Evolución Financiera Mensual',
        font: {
          size: titleSize,
          weight: 'bold' as const,
        },
        color: '#000',
      },
      subtitle: {
        display: true,
        text: 'Últimos 5 meses',
        font: {
          size: fontSize,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
        callbacks: {
          label(context: TooltipItem<'line'>) {
            const label = context.dataset.label || '';
            const value = new Intl.NumberFormat('es-CL', {
              style: 'currency',
              currency: 'CLP',
            }).format(context.parsed.y ?? 0);

            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !isPhone.value,
          text: 'Mes',
          font: {
            size: fontSize,
            weight: 'bold' as const,
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        title: {
          display: !isPhone.value,
          text: 'Monto (CLP)',
          font: {
            size: fontSize,
            weight: 'bold' as const,
          },
        },
        beginAtZero: true,
        ticks: {
          callback(value: string | number) {
            return new Intl.NumberFormat('es-CL', {
              style: 'currency',
              currency: 'CLP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(Number(value));
          },
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  };
});
</script>

<template>
  <div :style="{ height: isPhone ? '260px' : '400px' }">
    <Line
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>
